import {
  EntitlementGroupingSetConnectionFragment,
  EntitlementGroupingSetListQueryVariables,
  Ns_Ls_Entitlement,
  Ns_Ls_StandardEntitlementNames as StandardEntitlementNames,
  Ns_Ls_StandardProductIds as StandardProductIds,
  useEntitlementGroupingSetListQuery
} from "generated/graphql";
import {
  AvailableEntitlementNames,
  EntitlementAssignmentType,
  EntitlementState,
  EntitlementType,
  EntitlementValueType,
  getEntitlementAssignmentType,
  getEntitlementName,
  getEntitlementState,
  getEntitlementType,
  getEntitlementValueType,
  getProductName,
  isEntitlementAssigned,
  isEntitlementAwaitingRelease,
  isEntitlementNormal,
  isEntitlementOrphaned,
  isEntitlementUsed,
  StandardEntitlementNameOptionType,
  StandardEntitlementNameToOptionTypeMap,
  toEntitlementAssignmentType,
  toEntitlementState,
  toEntitlementType,
  toEntitlementValueType,
  toStandardEntitlementName
} from "utilities/licenseServerUtils";
import { parseIntSecondsTimeMilliseconds } from "utilities/datetime";
import { useMemo } from "react";

export type EntitlementGroupingAssignmentSet = {
  groupingId: string;
  totalAssignedValueSum: number;
  assignedCount: number;
  pendingAssignmentCount: number;
  pendingReleaseCount: number;
  removedCount: number;
  entitlements: Array<Entitlement>;
};

export type Entitlement = Omit<
  Ns_Ls_Entitlement,
  | "__typename"
  | "creationTime"
  | "lastModifiedTime"
  | "begins"
  | "expires"
  | "assignedToTenant"
> & {
  nameValue: string | undefined;
  productName: string | undefined;
  typeEnum: EntitlementType;
  typeName: string | undefined;
  stateEnum: EntitlementState;
  stateName: string | undefined;
  valueTypeEnum: EntitlementValueType;
  valueTypeName: string | undefined;
  assignmentTypeEnum: EntitlementAssignmentType;
  assignmentTypeName: string | undefined;

  creationTime: number;
  lastModifiedTime: number;
  begins: number;
  expires: number;

  readonly counted: boolean;
  readonly awaitingRelease: boolean;
  readonly assigned: boolean;
  readonly normal: boolean;
  readonly used: boolean;
  readonly orphaned: boolean;
  readonly assignable: boolean;
};

export type EntitlementGrouping = {
  tenantId: string;
  groupingId: string;
  nameId: string;
  name: string | undefined;
  type: EntitlementType;
  typeName: string | undefined;
  productId: string;
  productName: string | undefined;
  productVersion: string;
  activationCode: string;
  mappingId1: string;
  mappingId2: string;
  attributeCount: number;
  attributeType: EntitlementValueType;
  attributeTypeName: string | undefined;
  assignedCount: number;
  assignedProductCount: number;
  pendingAssignmentCount: number;
  pendingAssignmentProductCount: number;
  pendingReleaseCount: number;
  pendingReleaseProductCount: number;
  removedCount: number;
  removedProductCount: number;
  availableCount: number;
  lastModifiedTime: number;
  creationTime: number;
  begins: number;
  expires: number;
  assignmentType: EntitlementAssignmentType;
  assignmentTypeName: string | undefined;
  isUnlimitedBoolean: boolean;
  entitlements: Array<Entitlement>;
  assignmentMap: Map<String, EntitlementGroupingAssignmentSet>;
};

export type EntitlementGroupingEdge = {
  cursor: string;
  node: EntitlementGrouping;
};

export function toEntitlement(e: Ns_Ls_Entitlement): Entitlement {
  const typeEnum = toEntitlementType(e.type);
  const stateEnum = toEntitlementState(e.state);
  const valueTypeEnum = toEntitlementValueType(e.valueType);
  const assignmentTypeEnum = toEntitlementAssignmentType(e.assignedToTenant);
  return {
    ...e,
    nameValue: getEntitlementName(e.name),
    productName: getProductName(e.productId),
    typeEnum: typeEnum,
    typeName: getEntitlementType(typeEnum),
    stateEnum: stateEnum,
    stateName: getEntitlementState(stateEnum),
    valueTypeEnum: valueTypeEnum,
    valueTypeName: getEntitlementValueType(valueTypeEnum),
    assignmentTypeEnum: assignmentTypeEnum,
    assignmentTypeName: getEntitlementAssignmentType(assignmentTypeEnum),
    creationTime: parseIntSecondsTimeMilliseconds(e.creationTime),
    lastModifiedTime: parseIntSecondsTimeMilliseconds(e.lastModifiedTime),
    begins: parseIntSecondsTimeMilliseconds(e.begins),
    expires: parseIntSecondsTimeMilliseconds(e.expires),
    get counted(): boolean {
      return this.valueTypeEnum === EntitlementValueType.Counted;
    },
    get awaitingRelease(): boolean {
      return isEntitlementAwaitingRelease(this.stateEnum);
    },
    get assigned(): boolean {
      return isEntitlementAssigned(this.stateEnum);
    },
    get normal(): boolean {
      return isEntitlementNormal(this.stateEnum);
    },
    get used(): boolean {
      return isEntitlementUsed(this.stateEnum);
    },
    get orphaned(): boolean {
      return isEntitlementOrphaned(this.stateEnum);
    },
    get assignable(): boolean {
      return (
        this.normal &&
        this.assignmentTypeEnum !== EntitlementAssignmentType.Account
      );
    }
  };
}

export const getEntitlementGroupingConnection = (
  connection: Omit<EntitlementGroupingSetConnectionFragment, "__typename">
) => {
  const entitlementMap = new Map<string, Entitlement>();
  const edgesMap = new Map();

  const edges: Array<EntitlementGroupingEdge> = connection.edges.map(edge => {
    const grouping = edge.node;

    const entitlements: Array<Entitlement> = grouping.entitlements.map(e => {
      const entitlement = toEntitlement(e);
      entitlementMap.set(entitlement.recordId, entitlement);
      return entitlement;
    });

    // There should always be at least one entitlement
    const entitlement = entitlements[0];

    const availableCount = grouping.isUnlimitedBoolean
      ? -1
      : entitlement.assignmentTypeEnum === EntitlementAssignmentType.Account
      ? 0
      : grouping.availableValueSum; // - grouping.awaitingReleaseValueSum;

    const entitlementGrouping: EntitlementGrouping = {
      tenantId: entitlement.tenantId,
      groupingId: entitlement.groupingId,
      nameId: entitlement.name,
      name: entitlement.nameValue,
      type: entitlement.typeEnum,
      typeName: entitlement.typeName,
      productId: entitlement.productId,
      productName: entitlement.productName,
      productVersion: entitlement.productVersion,
      activationCode: entitlement.activationCode,
      mappingId1: entitlement.mappingId1,
      mappingId2: entitlement.mappingId2,
      attributeCount: grouping.valueSum,
      attributeType: entitlement.valueTypeEnum,
      attributeTypeName: entitlement.valueTypeName,
      assignedCount: grouping.totalAssignedValueSum,
      pendingAssignmentCount: grouping.awaitingReassignValueSum,
      pendingReleaseCount: grouping.awaitingReleaseValueSum,
      removedCount: grouping.orphanedValueSum,
      availableCount: availableCount,
      lastModifiedTime: entitlement.lastModifiedTime,
      creationTime: entitlement.creationTime,
      begins: entitlement.begins,
      expires: entitlement.expires,
      assignmentType: entitlement.assignmentTypeEnum,
      assignmentTypeName: entitlement.assignmentTypeName,
      isUnlimitedBoolean: grouping.isUnlimitedBoolean,
      assignedProductCount: 0,
      pendingAssignmentProductCount: 0,
      pendingReleaseProductCount: 0,
      removedProductCount: 0,
      entitlements: entitlements,
      assignmentMap: new Map()
    };

    grouping.assignmentGroupingSets.forEach(assignmentGrouping => {
      const assignmentSet: EntitlementGroupingAssignmentSet = {
        totalAssignedValueSum: assignmentGrouping.totalAssignedValueSum,
        assignedCount: assignmentGrouping.totalAssignedValueSum,
        groupingId: assignmentGrouping.groupingId,
        pendingAssignmentCount: assignmentGrouping.awaitingReassignValueSum,
        pendingReleaseCount: assignmentGrouping.awaitingReleaseValueSum,
        removedCount: assignmentGrouping.orphanedValueSum,
        entitlements: assignmentGrouping.entitlementIds.map(
          entitlementId => entitlementMap.get(entitlementId)!
        )
      };
      if (assignmentSet.assignedCount !== 0) {
        entitlementGrouping.assignedProductCount++;
      }
      if (assignmentSet.pendingAssignmentCount !== 0) {
        entitlementGrouping.pendingAssignmentProductCount++;
      }
      if (assignmentSet.pendingReleaseCount !== 0) {
        entitlementGrouping.pendingReleaseProductCount++;
      }
      if (assignmentSet.removedCount !== 0) {
        entitlementGrouping.removedProductCount++;
      }
      entitlementGrouping.assignmentMap.set(
        assignmentGrouping.recordId,
        assignmentSet
      );
    });

    const entInfoEdge: EntitlementGroupingEdge = {
      node: entitlementGrouping,
      cursor: edge.cursor
    };
    edgesMap.set(entitlementGrouping.groupingId, entInfoEdge);

    return entInfoEdge;
  });

  return {
    searchResult: {
      totalCount: connection.totalCount,
      pageInfo: connection.pageInfo,
      edges: edges
    }
  };
};

export type EntitlementGroupingListQueryVariables = Omit<
  EntitlementGroupingSetListQueryVariables,
  "assignedToTenant"
> & {};

export const useEntitlementGroupingList = (
  variables: EntitlementGroupingListQueryVariables,
  options?: {}
) => {
  const queryResults = useEntitlementGroupingSetListQuery({
    variables: variables,
    ...options,
    pause: !variables.tenantId
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.searchResult;
    if (sourceData) {
      result = getEntitlementGroupingConnection(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};

export type GetEntitlementGroupingQueryVariables = Omit<
  EntitlementGroupingListQueryVariables,
  "assignedToTenant"
> & {};

export const useGetEntitlementGroupingQuery = (
  variables: GetEntitlementGroupingQueryVariables,
  options?: {}
) => {
  const queryResults = useEntitlementGroupingList(variables, options);
  let data;
  const queryData = queryResults?.data?.searchResult;
  if (queryData) {
    data = {
      entitlement: queryData.edges.length > 0 ? queryData.edges[0].node : null
    };
  } else {
    data = undefined;
  }
  return { ...queryResults, data };
};

export type ProductTypeSelector = StandardProductIds | "";

// can we use a class or should we use a type with a function that provides default values
export type SupportedEntitlementConfig = {
  readonly productType: ProductTypeSelector;
  readonly productTypeEnabled: boolean;
  readonly productVersionEnabled: boolean;
  readonly activationKeyEnabled: boolean;
  readonly skuEnabled: boolean;
  readonly orderNumberEnabled: boolean;
  readonly licenseType: EntitlementType;
  readonly licenseTypeEnabled: boolean;
  readonly assignmentType: EntitlementAssignmentType;
  readonly assignmentTypeEnabled: boolean;
  readonly valueType: EntitlementValueType;
  readonly valueTypeEnabled: boolean;
  readonly valueTypeUnlimited: boolean;
  readonly valueTypeUnlimitedEnabled: boolean;
  readonly valueEnabled: boolean;
  readonly stringEnabled: boolean;
  readonly startDateEnabled: boolean;
  readonly startDateRequired: boolean;
  readonly expiresEnabled: boolean;
  readonly expiresRequired: boolean;
};

export function createSupportedEntitlementConfig(
  productType: ProductTypeSelector,
  productVersionEnabled: boolean,
  licenseType: EntitlementType,
  assignmentType: EntitlementAssignmentType,
  valueType: EntitlementValueType,
  valueEnabled: boolean,
  valueTypeUnlimited: boolean,
  valueTypeUnlimitedEnabled: boolean
): SupportedEntitlementConfig {
  return {
    productType: productType,
    productTypeEnabled: false,
    productVersionEnabled: productVersionEnabled,

    activationKeyEnabled: true,
    skuEnabled: true,
    orderNumberEnabled: true,

    licenseType: licenseType,
    licenseTypeEnabled: false,
    assignmentType: assignmentType,
    assignmentTypeEnabled: false,

    valueType: valueType,
    valueTypeEnabled: false,
    valueEnabled: valueEnabled,
    valueTypeUnlimited: valueTypeUnlimited,
    valueTypeUnlimitedEnabled: valueTypeUnlimitedEnabled,
    stringEnabled: false,
    startDateEnabled: true,
    startDateRequired: true,
    expiresRequired: true,
    expiresEnabled: true
  };
}

const supportedEntitlementConfigMap = new Map<
  StandardEntitlementNameOptionType,
  SupportedEntitlementConfig
>([
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.AudioDevices
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiManager,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.ClaritiManagerBaseLicense
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiManager,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Account,
      EntitlementValueType.Boolean,
      true,
      true,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.LongTermLicensing
    )!,
    createSupportedEntitlementConfig(
      "",
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Account,
      EntitlementValueType.Boolean,
      false,
      true,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.MediaEncryption
    )!,
    createSupportedEntitlementConfig(
      "",
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Account,
      EntitlementValueType.Boolean,
      false,
      true,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.PerpetualConcurrentCalls
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiCe,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.PerpetualConcurrentVmrs
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiCe,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.SubscriptionConcurrentAudioCalls
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiCe,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.SubscriptionConcurrentCalls
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiCe,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.SubscriptionConcurrentVmrs
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiCe,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ],
  [
    StandardEntitlementNameToOptionTypeMap.get(
      StandardEntitlementNames.VideoDevices
    )!,
    createSupportedEntitlementConfig(
      StandardProductIds.ClaritiManager,
      false,
      EntitlementType.Normal,
      EntitlementAssignmentType.Product,
      EntitlementValueType.Counted,
      true,
      false,
      false
    )
  ]
]);

export const SupportedEntitlementNameOptionTypes: Array<StandardEntitlementNameOptionType> = Array.from(
  supportedEntitlementConfigMap.keys()
)
  .filter(optionType => AvailableEntitlementNames.has(optionType.type))
  .sort((a, b) => a.text.localeCompare(b.text));

export function getSupportedEntitlementConfigMap(): Map<
  StandardEntitlementNameOptionType,
  SupportedEntitlementConfig
> {
  return supportedEntitlementConfigMap;
}

export function getSupportedEntitlementConfig(
  name: string
): SupportedEntitlementConfig | undefined {
  const entitlementNameOptionType = StandardEntitlementNameToOptionTypeMap.get(
    toStandardEntitlementName(name)
  );
  return entitlementNameOptionType
    ? getSupportedEntitlementConfigMap().get(entitlementNameOptionType)
    : undefined;
}
